import {
  ArrowForwardRounded,
  DownloadRounded,
  MoreVertOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import type { ChangeEvent, MouseEvent } from "react";

import type {
  Order,
  OrderStatusHistory,
  ProductAddon,
  ProductAddonType,
} from "../../types/order";
import { valueOrDash } from "../../utils/decimal";
import { downloadOrderXml } from "../../utils/orderXmlBuilder";
import EmptyState from "./EmptyState";
import {
  type OrderListFilters,
  STATUS_CHIP_CONFIG,
} from "./OrderFilterControls";
import TableLoadingSkeleton, {
  type TableSkeletonColumn,
} from "./TableLoadingSkeleton";

export interface OrderTableProps {
  visibleOrders: Order[];
  isLoading: boolean;
  filters: OrderListFilters;
  totalRecords: number;
  selectedIds: string[];
  allSelected: boolean;
  isIndeterminate: boolean;
  onSelectAll: (checked: boolean) => void;
  onSelectRow: (id: string) => void;
  onOpenActionMenu: (order: Order, event: MouseEvent<HTMLElement>) => void;
  onPageChange: (_: unknown, page: number) => void;
  onRowsPerPageChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

const HEADER_CELLS = [
  { label: "Actions", width: 80, align: "center" as const },
  { label: "No.", width: 96, align: "center" as const },
  { label: "Order ID", width: 153 },
  { label: "Payment Term", width: 153 },
  { label: "ECP Code", width: 120 },
  { label: "Name Customer", width: 190 },
  { label: "Order Reference", width: 190 },
  { label: "State Order", width: 166, align: "center" as const },
  { label: "Details", width: 220 },
  { label: "Tracking No.", width: 267 },
  { label: "Order Type", width: 120 },
  { label: "Lens Type for Order", width: 170 },
  { label: "Index", width: 90, align: "center" as const },
  { label: "Product Auto Completed", width: 285 },
  { label: "Chromatic", width: 190 },
  { label: "Add VA", width: 140 },
  { label: "Add Ins", width: 140 },
  { label: "XML", width: 161, align: "center" as const },
  { label: "Order Time", width: 190, align: "center" as const },
  { label: "Status Update Date", width: 190, align: "center" as const },
  { label: "Latest Status Change", width: 460 },
] as const;

function formatDateTime(value?: string) {
  return value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-";
}

function formatRelativeDateTime(value?: string) {
  if (!value) return "-";
  const target = dayjs(value);
  if (!target.isValid()) return "-";

  const now = dayjs();
  const time = target.format("HH:mm");

  if (target.isSame(now, "day")) return `Today, ${time}`;
  if (target.isSame(now.subtract(1, "day"), "day")) return `Yesterday, ${time}`;
  if (target.isSame(now, "year")) return `${target.format("D MMM")}, ${time}`;
  return `${target.format("D MMM YYYY")}, ${time}`;
}

function formatStatusLabel(value?: string) {
  return value
    ? value
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "-";
}

function formatChangedBy(entry?: OrderStatusHistory) {
  const user = entry?.changed_by;
  if (!user) return "-";

  const name = user.name || user.id || "-";
  return user.role ? `${name} (${user.role})` : name;
}

const STATUS_PILL_FALLBACK = { bg: "#F0F0F0", color: "#555" } as const;

function StatusPill({ status }: { status?: string }) {
  const config = status ? STATUS_CHIP_CONFIG[status] : undefined;
  const palette = config ?? STATUS_PILL_FALLBACK;
  const label = config?.label ?? formatStatusLabel(status);

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 20,
        px: 0.75,
        borderRadius: 1,
        bgcolor: palette.bg,
        color: palette.color,
        fontSize: 10,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}

function LatestStatusCell({ order }: { order: Order }) {
  const { entry, previousEntry } = getLatestStatusChange(order);

  if (!entry) {
    return (
      <Box component="span" sx={{ color: "#9CA3AF", fontSize: 12 }}>
        —
      </Box>
    );
  }

  const LABEL_SX = {
    color: "#9CA3AF",
    fontSize: 12,
    fontWeight: 400,
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        whiteSpace: "normal",
        py: 0.25,
        lineHeight: 1.4,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
        {previousEntry?.status ? (
          <>
            <StatusPill status={previousEntry.status} />
            <ArrowForwardRounded sx={{ fontSize: 14, color: "#9CA3AF" }} />
          </>
        ) : null}
        <StatusPill status={entry.status} />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          flexWrap: "wrap",
          fontSize: 12,
          color: "#25272C",
        }}
      >
        <Box sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.5 }}>
          <Box component="span" sx={LABEL_SX}>
            By
          </Box>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {formatChangedBy(entry)}
          </Box>
        </Box>
        <Box component="span" sx={{ color: "#D1D5DB" }}>
          ·
        </Box>
        <Tooltip
          title={formatDateTime(entry.changed_at)}
          placement="top"
          arrow
          disableInteractive
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "baseline",
              gap: 0.5,
              cursor: "default",
            }}
          >
            <Box component="span" sx={LABEL_SX}>
              At
            </Box>
            <Box component="span">{formatRelativeDateTime(entry.changed_at)}</Box>
          </Box>
        </Tooltip>
      </Box>

      {entry.remarks ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.5,
            fontSize: 12,
            color: "#4B5563",
          }}
        >
          <Box component="span" sx={LABEL_SX}>
            Note
          </Box>
          <Box component="span" sx={{ fontStyle: "italic" }}>
            {entry.remarks}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

function getSortedStatusChanges(order: Order) {
  return [...(order.status_history ?? [])]
    .filter((entry) => entry.changed_at || entry.status)
    .sort(
      (a, b) =>
        new Date(b.changed_at || 0).getTime() -
        new Date(a.changed_at || 0).getTime(),
    );
}

function getLatestStatusChange(order: Order) {
  const sortedLogs = getSortedStatusChanges(order);

  return {
    entry: sortedLogs[0],
    previousEntry: sortedLogs[1],
  };
}

function formatCustomerName(order: Order) {
  const fullName = [order.customer?.name, order.customer?.surname]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || order.customer?.shop_name || "-";
}

function formatOrderType(value?: string) {
  return value
    ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
    : "-";
}

function formatLensType(value?: string) {
  if (value === "PAIR") return "Pair of Lenses";
  if (value === "SINGLE") return "Single Lens";
  return "-";
}

function formatIndex(order: Order) {
  const detailIndex = valueOrDash(order.detail?.index);
  if (detailIndex !== "-") return detailIndex;
  return valueOrDash(order.product?.index_code);
}

function collectAddons(order: Order, type: ProductAddonType) {
  const allAddons = (order.addons || [])
    .map((item) => item.product_addons)
    .filter((addon): addon is ProductAddon => addon?.type === type);

  const seen = new Set<string>();
  return allAddons.filter((addon) => {
    const key =
      addon.id ||
      addon.custom_product ||
      addon.custom_product_search ||
      addon.index_key;

    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function formatAddons(order: Order, type: ProductAddonType) {
  const items = collectAddons(order, type).map(
    (addon) => addon.custom_product || addon.index_key || "",
  );

  return items.length ? items.join(", ") : "-";
}

function getStatusUpdatedAt(order: Order) {
  if (order.status_history?.length) {
    const latest = [...order.status_history]
      .filter((entry) => entry.changed_at)
      .sort(
        (a, b) =>
          new Date(b.changed_at || 0).getTime() -
          new Date(a.changed_at || 0).getTime(),
      )[0];

    if (latest?.changed_at) return latest.changed_at;
  }

  return order.updated_at;
}

const ORDER_TABLE_SKELETON_COLUMNS: TableSkeletonColumn[] = [
  { key: "checkbox", isCheckbox: true },
  { key: "actions", width: 80, align: "center" },
  ...HEADER_CELLS.map((cell) => ({
    key: cell.label.toLowerCase().replace(/\s+/g, "-"),
    width: cell.width,
    align: "align" in cell ? (cell as any).align : undefined,
  })),
];

export default function OrderTable({
  visibleOrders,
  isLoading,
  filters,
  totalRecords,
  selectedIds,
  allSelected,
  isIndeterminate,
  onSelectAll,
  onSelectRow,
  onOpenActionMenu,
  onPageChange,
  onRowsPerPageChange,
}: OrderTableProps) {
  const theme = useTheme();
  const getStatusChipConfig = (status?: string) => {
    if (!status) return null;
    return STATUS_CHIP_CONFIG[status.toUpperCase()];
  };

  if (isLoading)
    return <TableLoadingSkeleton columns={ORDER_TABLE_SKELETON_COLUMNS} />;

  if (visibleOrders.length === 0) {
    return (
      <Box sx={{ borderTop: `1px solid ${theme.vars.palette.divider}` }}>
        <EmptyState variant="default" />
      </Box>
    );
  }

  return (
    <>
      <TableContainer
        sx={{ borderTop: `1px solid ${theme.vars.palette.divider}` }}
      >
        <Table sx={{ minWidth: 3980, "& td, & th": { whiteSpace: "nowrap" } }}>
          <TableHead>
            <TableRow
              sx={{
                "& .MuiTableCell-root": {
                  height: 52,
                  borderBottom: `1px solid ${theme.vars.palette.divider}`,
                  color: "#25272C",
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  lineHeight: 1.4,
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={isIndeterminate}
                  onChange={(event) => onSelectAll(event.target.checked)}
                  sx={{
                    "&.Mui-checked": { color: "#1976D2" },
                    "&.MuiCheckbox-indeterminate": { color: "#1976D2" },
                  }}
                />
              </TableCell>
              {HEADER_CELLS.map((column) => (
                <TableCell
                  key={column.label}
                  align={"align" in column ? (column as any).align : undefined}
                  sx={{ width: column.width }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleOrders.map((order, index) => (
              <TableRow
                key={order.id || `row-${index}`}
                selected={order.id ? selectedIds.includes(order.id) : false}
                sx={{
                  "&.Mui-selected, &.Mui-selected:hover": {
                    backgroundColor: "#F9FAFB",
                  },
                  "& .MuiTableCell-root": {
                    height: 79,
                    borderBottom: `1px solid ${theme.vars.palette.divider}`,
                    fontSize: 12,
                    color: "#25272C",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={order.id ? selectedIds.includes(order.id) : false}
                    onChange={() => order.id && onSelectRow(order.id)}
                    sx={{ "&.Mui-checked": { color: "#1976D2" } }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ width: 80 }}>
                  <IconButton
                    size="small"
                    onClick={(event) => onOpenActionMenu(order, event)}
                  >
                    <MoreVertOutlined fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell align="center" sx={{ width: 96 }}>
                  {index + 1 + filters.page * filters.page_size}
                </TableCell>
                <TableCell sx={{ width: 153 }}>
                  {order.order_id || "-"}
                </TableCell>
                <TableCell sx={{ width: 153 }}>
                  {order.payment_tag_name || "-"}
                </TableCell>
                <TableCell sx={{ width: 120 }}>
                  {order.customer?.ecp_code || "-"}
                </TableCell>
                <TableCell sx={{ width: 190 }}>
                  {formatCustomerName(order)}
                </TableCell>
                <TableCell sx={{ width: 190 }}>
                  {order.reference_no || "-"}
                </TableCell>
                <TableCell align="center" sx={{ width: 166 }}>
                  {(() => {
                    const statusConfig = getStatusChipConfig(order.status);
                    const fallbackLabel = order.status
                      ? formatStatusLabel(order.status)
                      : "Unknown";

                    return (
                      <Chip
                        label={statusConfig?.label || fallbackLabel}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: 10,
                          borderRadius: 1,
                          backgroundColor: statusConfig?.bg || "#D9EBFF",
                          color: statusConfig?.color || "#2481EA",
                        }}
                      />
                    );
                  })()}
                </TableCell>
                <TableCell sx={{ width: 220 }}>
                  {order.remarks || "-"}
                </TableCell>
                <TableCell sx={{ width: 267 }}>
                  {order.tracking_number?.length
                    ? order.tracking_number.join(", ")
                    : "-"}
                </TableCell>
                <TableCell sx={{ width: 120 }}>
                  {formatOrderType(order.order_type)}
                </TableCell>
                <TableCell sx={{ width: 170 }}>
                  {formatLensType(order.lens_number)}
                </TableCell>
                <TableCell align="center" sx={{ width: 90 }}>
                  {formatIndex(order)}
                </TableCell>
                <TableCell sx={{ width: 285 }}>
                  {order.product?.custom_description ||
                    order.product?.description ||
                    order.detail?.purchased_item ||
                    "-"}
                </TableCell>
                <TableCell sx={{ width: 190 }}>
                  {order.product?.chromatic_code ||
                    order.detail?.transition ||
                    "-"}
                </TableCell>
                <TableCell sx={{ width: 140 }}>
                  {formatAddons(order, "VALUE_ADD")}
                </TableCell>
                <TableCell sx={{ width: 140 }}>
                  {formatAddons(order, "INSTRUCTION")}
                </TableCell>
                <TableCell align="center" sx={{ width: 161 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => void downloadOrderXml(order)}
                    startIcon={<DownloadRounded sx={{ fontSize: 14 }} />}
                    sx={{
                      minWidth: 0,
                      px: 1.25,
                      height: 24,
                      borderRadius: 999,
                      backgroundColor: "#111111",
                      color: "#FFFFFF",
                      fontSize: 10,
                      fontWeight: 600,
                      textTransform: "none",
                      "& .MuiButton-startIcon": {
                        marginRight: 0.5,
                      },
                      "&:hover": {
                        backgroundColor: "#2A2A2A",
                      },
                    }}
                  >
                    Download
                  </Button>
                </TableCell>
                <TableCell align="center" sx={{ width: 190 }}>
                  {formatDateTime(order.created_at)}
                </TableCell>
                <TableCell align="center" sx={{ width: 190 }}>
                  {formatDateTime(getStatusUpdatedAt(order))}
                </TableCell>
                <TableCell
                  sx={{
                    width: 460,
                    "&.MuiTableCell-root": { whiteSpace: "normal" },
                  }}
                >
                  <LatestStatusCell order={order} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {visibleOrders.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalRecords}
          rowsPerPage={filters.page_size}
          page={filters.page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          sx={{
            "& .MuiTablePagination-toolbar": {
              minHeight: 44,
              px: 2,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: 12,
                color: "#25272C",
              },
          }}
        />
      )}
    </>
  );
}
